import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material';
import { ComponentType } from '@angular/cdk/portal';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

import { DialogPanelComponentContent } from './../models/dialogs/DialogPanelComponentContent';
import { SimpleModalComponent } from './../components/dialogs/simple/simple.component';
import { ConfirmComponent } from './../components/dialogs/confirm/confirm.component';
import { TrReceiptComponent } from '../components/dialogs/tr-receipt/tr-receipt.component';
import { ListInfoComponent } from './../components/dialogs/list-info/list-info.component';
import { ConfirmWithOptionsComponent } from '../components/dialogs/cards/confirm-with-options/confirm-with-options.component';
import { IDialog } from './../models/dialogs/IDialog';
import { IconType } from './../enum/icon-type/icon-type.enum';

type ConfirmDestructiveActionOptions = {
    hasBackdropClick?: boolean,
    onBackdropClick?: () => any,
}

@AutoUnsubscribe()
@Injectable()
export class DialogsService {
    private confirmDestructiveActionSubscription: Subscription;
    private showComponentAsAsidePanelSubscription: Subscription;

    constructor(private dialog: MatDialog) { }

    /**
     * Confirmar ação do usuário
     * @param title título da modal (aceita HTML)
     * @param message mensagem da modal (aceita HTML)
     * @param confirmButtonLabel label do botão de confirmação
     * @param cancelButtonLabel label do botão de cancelamento da ação
     * @param iconIdOption id do icone que irá ser mostrado
     * @param onBackdropClick callback para ser chamado ao clicar no backdrop
     */
    public confirmDestructiveAction(
        title: string,
        message: string,
        confirmButtonLabel: string = 'Sim',
        cancelButtonLabel: string = 'Não',
        iconIdOption: string = 'icon_info',
        options: ConfirmDestructiveActionOptions = {
            hasBackdropClick: false,
        }
    ): Observable<any> {

        // tslint:disable-next-line: prefer-const
        let dialogRef = this.dialog.open(ConfirmComponent, {
            disableClose: true,
            data: {
                destructiveAction: true,
                iconId: iconIdOption,
            }
        });
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;
        dialogRef.componentInstance.cancelButtonLabel = cancelButtonLabel;
        dialogRef.componentInstance.confirmButtonLabel = confirmButtonLabel;
        dialogRef.componentInstance.iconId = iconIdOption;
        this.confirmDestructiveActionSubscription = dialogRef.backdropClick().subscribe(() => {
            if (options.hasBackdropClick) {
                if (options.onBackdropClick) {
                    options.onBackdropClick();
                }

                dialogRef.close();
            }
        });

        return dialogRef.afterClosed();
    }

    /**
     * Exibir um modal simples
     * @param title título da modal (aceita HTML)
     * @param message mensagem da modal (aceita HTML)
     * @param buttonLabel label do botão de confirmação
     */
    public showSimpleModal(
        iconIdOption: string = '',
        titleOption: string,
        messageOption: string,
        buttonLabelOption: string = 'Fechar',
        disableCloseOption = false,
        showCloseButtonOption: boolean = false,
        iconTypeOption: IconType = IconType.warning,
    ): Observable<any> {

        const dialogRef = this.dialog.open(
            SimpleModalComponent,
            {
                disableClose: disableCloseOption,
                data: {
                    iconId: iconIdOption,
                    title: titleOption,
                    message: messageOption,
                    buttonLabel: buttonLabelOption,
                    showCloseButton: showCloseButtonOption,
                    iconType: iconTypeOption,
                },
            },
        );

        return dialogRef.afterClosed();
    }

    /**
     * Confirmar ação do usuário
     * @param title título da modal (aceita HTML)
     * @param message mensagem da modal (aceita HTML)
     * @param confirmButtonLabel label do botão de confirmação
     * @param cancelButtonLabel label do botão de cancelamento da ação
     */
    public confirmAction(
        title: string,
        message: string,
        confirmButtonLabel: string = 'Sim',
        cancelButtonLabel: string = 'Não',
    ): Observable<any> {
        // tslint:disable-next-line: prefer-const
        let dialogRef = this.dialog.open(ConfirmComponent, { disableClose: true });
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;
        dialogRef.componentInstance.cancelButtonLabel = cancelButtonLabel;
        dialogRef.componentInstance.confirmButtonLabel = confirmButtonLabel;

        return dialogRef.afterClosed();
    }

    /**
     * Confirmar ação do usuário
     * @param title título da modal (aceita HTML)
     * @param content mensagem da modal (aceita HTML)
     * @param options Opções do modal (texto aceita HTML)
     * @param cancelButtonLabel label do botão de confirmação
     * @param confirmButtonLabel label do botão de cancelamento da ação
     */
    public confirmActionWithOptions(
        title: string,
        content: string,
        options: any[],
        cancelButtonLabel: string = 'Cancelar',
        confirmButtonLabel: string = 'Continuar',
    ): Observable<any> {
        const dialogRef = this.dialog.open(ConfirmWithOptionsComponent, { disableClose: true });
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.content = content;
        dialogRef.componentInstance.options = options;
        dialogRef.componentInstance.cancelButton = cancelButtonLabel;
        dialogRef.componentInstance.confirmButton = confirmButtonLabel;

        return dialogRef.componentInstance.subject;
    }

    /**
     * Exibe um component dentro de uma modal
     * @param component Componente a ser exibido
     */
    public showComponnent(component: ComponentType<IDialog>, data: any = null, disableClose = false) {
        return this.dialog.open(component, { data, disableClose });
    }

    /**
     * Exibe um component dentro de uma modal
     * @param component Componente a ser exibido
     */
    public showListInfoComponent(title: string, message: string[], customClassOp?: string) {
        // tslint:disable-next-line: prefer-const
        let dialogRef = this.dialog.open(ListInfoComponent, { disableClose: false, data: { customClass: customClassOp } });
        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.message = message;
        // return this.dialog.open(component);
    }

    /**
     * Mostra uma modal como um painel sobre a página
     * @param component Component a ser exibido
     * @param data Dados a serem passados para o construtor do component
     * @param alignRight alinhar a direita da tela
     * @param hasBackdrop fechar ao clicar no backdrop
     * @param onBackdropClick método a ser chamado ao clicar no backdrop
     */
    public showComponentAsAsidePanel(
        component: ComponentType<DialogPanelComponentContent>,
        alignRight: boolean,
        dataOp: any = null,
        disableCloseOp: boolean = true,
        hasBackdrop: boolean = true,
        onBackdropClick?: () => void,
    ) {

        let modalConfiguration = new MatDialogConfig();
        const heightOp = window.innerHeight + 'px';

        modalConfiguration = { data: dataOp, disableClose: disableCloseOp, position: { top: '0', right: '-100%' }, height: heightOp, hasBackdrop };

        if (!alignRight) {
            modalConfiguration.position = { top: '0', left: '-100%' };
        }

        const dialogReference = this.dialog.open(component, modalConfiguration);

        this.showComponentAsAsidePanelSubscription = dialogReference.backdropClick().subscribe(() => {
            if (hasBackdrop) {
                if (onBackdropClick) {
                    onBackdropClick();
                }

                dialogReference.close();
            }
        });

        return dialogReference;
    }

    /** Fechar todas as modals */
    public closeAll() {
        this.dialog.closeAll();
        $('html, body')
            .css('overflow', 'visible');
    }

    /**
     *
     * @param title Título do recibo (allow HTML)
     * @param message Mensagem do recibo (allow HTML)
     * @param ctaButtonLabel Label do botão principal
     * @param ctaLink Link para o botão principal
     * @param icon ícone
     * @param closeButtonLink Link do botão de fechar
     * @param secundaryCtaButtonLabel Label do cta secundário
     * @param secundaryCtaLink Link do botão secundário
     */
    public showReceipt(
        titleOp: string,
        messageOp: string,
        ctaButtonLabelOp: string,
        ctaLinkOp: string,
        closeButtonLinkOp: string,
        iconOp: string = 'icon_success',
        secundaryCtaButtonLabelOp: string = null,
        secundaryCtaLinkOp: string = null,
    ) {

        const payload = {
            title: titleOp,
            message: messageOp,
            ctaButtonLabel: ctaButtonLabelOp,
            ctaLink: ctaLinkOp,
            closeButtonLink: closeButtonLinkOp,
            secundaryCtaButtonLabel: secundaryCtaButtonLabelOp,
            secundaryCtaLink: secundaryCtaLinkOp,
            icon: iconOp,
        };
        return this.dialog.open(TrReceiptComponent, { data: payload });
    }
}
